import type { NostrEvent } from '@nostrify/nostrify';
import { useAppContext } from '@/hooks/useAppContext';
import { useCachedNostrQuery } from '@/hooks/useCachedNostrQuery';

export interface RepositoryEvent extends NostrEvent {
  kind: 30617;
}

interface UseNostrReposOptions {
  authorPubkey?: string;
  /** Maximum number of repositories to load. Default: 500 */
  limit?: number;
  /** Whether the query should run. Default: true */
  enabled?: boolean;
}

/**
 * Fetch NIP-34 repository announcements (kind 30617) from git-focused relays.
 * Uses IndexedDB caching for instant loading.
 *
 * When `authorPubkey` is provided, only repositories from that author are returned.
 * Otherwise, this returns a global list of repositories from all authors.
 */
export function useNostrRepos(options: UseNostrReposOptions = {}) {
  const { authorPubkey, limit = 500, enabled = true } = options;
  const { config } = useAppContext();

  const gitRelays = config.ngitServers.map((server) => `wss://${server}/`);

  const filter: { kinds: number[]; authors?: string[]; limit: number } = {
    kinds: [30617],
    limit,
  };

  if (authorPubkey) {
    filter.authors = [authorPubkey];
  }

  return useCachedNostrQuery({
    filters: [filter],
    relays: gitRelays,
    timeout: 8000,
    cacheMaxAge: 5 * 60 * 1000, // Cache for 5 minutes
    useStaleWhileRevalidate: true,
    queryOptions: {
      enabled: enabled && (!!authorPubkey || authorPubkey === undefined),
      select: (events) =>
        (events as NostrEvent[])
          .filter((e): e is RepositoryEvent => e.kind === 30617)
          .sort((a, b) => b.created_at - a.created_at),
    },
  });
}
